import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, CameraIcon } from '@heroicons/react/24/outline';
import { Html5Qrcode } from 'html5-qrcode';
import { apiService } from '../services/api';
import ErrorModal from './ErrorModal';

interface ValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplied: (code: string) => void;
  ticketId: number;
}

const ValidationModal: React.FC<ValidationModalProps> = ({
  isOpen,
  onClose,
  onApplied,
  ticketId,
}) => {
  const [validationCode, setValidationCode] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showScanner, setShowScanner] = useState(false);

  const qrCodeRef = useRef<Html5Qrcode | null>(null);
  const isStoppingRef = useRef(false);
  const initRetryCountRef = useRef(0);
  const initRetryTimeoutRef = useRef<number | null>(null);

  const resetState = useCallback(() => {
    setValidationCode('');
    setValidationError(null);
    setIsLoading(false);
    setApiError(null);
    setShowScanner(false);
  }, []);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    resetState();
    onClose();
  }, [isLoading, resetState, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 50);
    setValidationCode(digits);
    if (validationError) setValidationError(null);
  };

  const handleApply = async () => {
    if (validationCode.length < 9) {
      setValidationError('Validation code must be at least 9 digits');
      return;
    }

    setValidationError(null);
    setIsLoading(true);

    try {
      await apiService.applyCoupon({
        validationNumber: validationCode,
        ticketId,
      });
      onApplied(validationCode);
      resetState();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to apply validation';
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const stopScanner = useCallback(async () => {
    if (!qrCodeRef.current || isStoppingRef.current) return;
    isStoppingRef.current = true;
    try {
      await qrCodeRef.current.stop();
      await qrCodeRef.current.clear();
    } catch (error) {
      console.error('Error stopping QR scanner:', error);
    } finally {
      qrCodeRef.current = null;
      isStoppingRef.current = false;
    }
  }, []);

  const handleQRCodeScanned = useCallback(
    async (decodedText: string) => {
      await stopScanner();
      setShowScanner(false);

      const digits = decodedText.replace(/\D/g, '').slice(0, 50);
      if (digits) {
        setValidationCode(digits);
        setValidationError(null);
      }
    },
    [stopScanner]
  );

  useEffect(() => {
    const startScannerIfReady = () => {
      if (!showScanner || qrCodeRef.current) return;

      const container = document.getElementById('validation-qr-reader');
      if (!container) {
        if (initRetryCountRef.current < 10) {
          initRetryCountRef.current += 1;
          initRetryTimeoutRef.current = window.setTimeout(
            startScannerIfReady,
            50
          );
        } else {
          console.error('validation-qr-reader element not found after retries');
          setShowScanner(false);
        }
        return;
      }

      try {
        const qrCode = new Html5Qrcode('validation-qr-reader');
        qrCodeRef.current = qrCode;

        qrCode
          .start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: { width: 250, height: 250 } },
            (decodedText) => {
              handleQRCodeScanned(decodedText);
            },
            () => {}
          )
          .catch((err) => {
            console.error('Error starting QR scanner:', err);
            setApiError('Failed to access camera. Please check permissions.');
            setShowScanner(false);
            qrCodeRef.current = null;
          });
      } catch (e) {
        console.error('Failed to initialize Html5Qrcode:', e);
        setApiError('Failed to initialize scanner.');
        setShowScanner(false);
        qrCodeRef.current = null;
      }
    };

    if (showScanner) {
      initRetryCountRef.current = 0;
      startScannerIfReady();
    }

    return () => {
      if (initRetryTimeoutRef.current !== null) {
        window.clearTimeout(initRetryTimeoutRef.current);
        initRetryTimeoutRef.current = null;
      }
      void stopScanner();
    };
  }, [showScanner, handleQRCodeScanned, stopScanner]);

  useEffect(() => {
    if (!isOpen) {
      void stopScanner();
    }
  }, [isOpen, stopScanner]);

  if (!isOpen) return null;

  return (
    <>
      {/* Validation form dialog */}
      {!showScanner && (
        <Dialog open={true} onClose={handleClose} className="relative z-50">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded-xl p-6 max-w-sm w-full">
              <div className="flex items-center justify-between mb-1">
                <Dialog.Title className="text-black font-extrabold text-lg">
                  Add validation
                </Dialog.Title>
                <button
                  onClick={handleClose}
                  className="text-text-disabled hover:text-black"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <p className="text-text-disabled text-small mb-4 leading-snug">
                Enter the code from your sticker or coupon, or scan its QR /
                barcode.
              </p>

              <div className="mb-1">
                <label className="block text-[13px] font-semibold text-black mb-1.5">
                  Validation code
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={50}
                    value={validationCode}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                    placeholder="e.g. 123456789"
                    disabled={isLoading}
                    className={`input-field w-full pr-10 ${
                      validationError ? 'border-red-500' : ''
                    }`}
                  />
                  <button
                    onClick={() => setShowScanner(true)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 transition-colors"
                    type="button"
                    disabled={isLoading}
                  >
                    <CameraIcon className="h-6 w-6" />
                  </button>
                </div>
                {validationError && (
                  <p className="text-red-500 text-small mt-2">
                    {validationError}
                  </p>
                )}
              </div>

              <button
                onClick={handleApply}
                disabled={isLoading}
                className="btn-primary w-full mt-4"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Validating…
                  </span>
                ) : (
                  'Apply validation'
                )}
              </button>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}

      {/* QR Scanner dialog */}
      {showScanner && (
        <Dialog
          open={true}
          onClose={async () => {
            await stopScanner();
            setShowScanner(false);
          }}
          className="relative z-50"
        >
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded-xl p-6 max-w-sm w-full">
              <Dialog.Title className="text-black font-bold text-xl mb-4 text-center">
                Scan QR Code
              </Dialog.Title>

              <div className="mb-4">
                <div
                  id="validation-qr-reader"
                  className="w-full rounded-lg overflow-hidden"
                />
              </div>

              <button
                onClick={async () => {
                  await stopScanner();
                  setShowScanner(false);
                }}
                className="btn-secondary w-full"
              >
                Cancel
              </button>
            </Dialog.Panel>
          </div>
        </Dialog>
      )}

      {/* API error modal */}
      {apiError && (
        <ErrorModal message={apiError} onClose={() => setApiError(null)} />
      )}
    </>
  );
};

export default ValidationModal;
